import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';

export default function AuditorSelfServePage() {
  const faqs = [
    {
      question: 'What is guest mode?',
      answer: 'Guest mode creates a temporary, anonymous session for evidence export. Auditors can generate evidence packs without creating a permanent account.'
    },
    {
      question: 'Which tools are available in guest mode?',
      answer: 'Guest sessions support PR approvals reports and branch protection snapshots.'
    },
    {
      question: 'How long does guest access last?',
      answer: 'Guest sessions and their evidence packs are retained for 24 hours.'
    },
    {
      question: 'Does guest mode require write access?',
      answer: 'No. Guest mode uses the read-only GitHub App installation and does not modify repositories.'
    }
  ];

  return (
    <MarketingLayout
      title="Auditor Self-Serve Evidence Packs | EvidentTrail"
      description="Guest mode enables anonymous evidence exports for PR approvals and branch protection snapshots with 24-hour retention."
      h1="Auditor Self-Serve Evidence Packs"
      heroText="Use guest mode to generate evidence packs without granting repo write access or permanent accounts."
      type="article"
    >
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', url: 'https://evidenttrail.com/' },
          { name: 'Auditor Self-Serve', url: 'https://evidenttrail.com/auditor-self-serve' }
        ]}
      />
      <FaqJsonLd items={faqs} />
      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>How Guest Mode Works</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Guest mode creates a temporary, anonymous session for evidence export. Auditors can
              install the read-only GitHub App, select repositories, and generate evidence packs
              without creating a permanent account. Guest sessions are retained for 24 hours and
              support PR approvals reports and branch protection snapshots.
            </p>
            <div className={tokens.card}>
              <h3 className={`${tokens.heading.h3} mb-3`}>Guest Mode Facts</h3>
              <ul className="space-y-2 text-sm text-slate-400">
                <li>• No account required</li>
                <li>• Read-only GitHub App installation</li>
                <li>• 24-hour session retention</li>
                <li>• Single report for one repository</li>
                <li>• PR approvals and branch protection snapshot tools</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Evidence Pack Outputs</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Guest mode exports the same evidence pack formats as authenticated sessions. Each pack
              includes PDF, CSV, JSON, and manifest files with SHA-256 checksums so auditors can
              verify integrity without direct repository access.
            </p>
            <p className={`${tokens.text.base} mb-6`}>
              For time-limited sharing and access controls, see our{' '}
              <Link to="/secure-evidence-sharing" className="text-primary-400 hover:underline">secure evidence sharing</Link>
              guidance.
            </p>
          </div>
        </div>
      </section>

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Data Boundaries</h2>
            <p className={`${tokens.text.base} mb-6`}>
              Guest mode uses the same metadata-only capture as the core platform. It does not store
              source code, prompts, or free-text review comments. Evidence packs remain available for
              the 24-hour guest session window and expire afterward.
            </p>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>When to Use the Core Platform</h2>
            <p className={`${tokens.text.base} mb-6`}>
              The core platform is designed for continuous evidence history. It stores audit
              timeline data, drift changes, exception records, and plan-based evidence pack retention
              beyond the 24-hour guest session window.
            </p>
          </div>
        </div>
      </section>

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <div className="max-w-3xl mx-auto">
            <h2 className={`${tokens.heading.h2} mb-6`}>Frequently Asked Questions</h2>
            <div className="space-y-6">
              {faqs.map((item) => (
                <div key={item.question} className={tokens.card}>
                  <h3 className={`${tokens.heading.h3} mb-2`}>{item.question}</h3>
                  <p className={tokens.text.base}>{item.answer}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className={tokens.section}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Evidence Options</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/tools/pr-approvals-report" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">PR Approvals Tool</h3>
              <p className="text-sm text-slate-400">Generate PR approval evidence packs in guest mode.</p>
            </Link>
            <Link to="/tools/branch-protection-snapshot" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Branch Protection Tool</h3>
              <p className="text-sm text-slate-400">Export branch protection snapshots without write access.</p>
            </Link>
            <Link to="/continuous-audit-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Continuous Evidence</h3>
              <p className="text-sm text-slate-400">See how the core platform maintains ongoing history.</p>
            </Link>
          </div>
        </div>
      </section>

      <section className={tokens.sectionAlt}>
        <div className={tokens.container}>
          <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Outcomes</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link to="/continuous-audit-evidence" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Continuous Audit Evidence</h3>
              <p className="text-sm text-slate-400">Keep approvals, drift, and exceptions in one timeline.</p>
            </Link>
            <Link to="/engineering-audit-toil" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">Reduce Audit Toil</h3>
              <p className="text-sm text-slate-400">Replace manual screenshots with automated exports.</p>
            </Link>
            <Link to="/iso-27001-github-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
              <h3 className="text-white font-medium mb-2">ISO 27001 GitHub Guide</h3>
              <p className="text-sm text-slate-400">Align guest evidence exports to ISO 27001 A.8.32 requirements.</p>
            </Link>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
